import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, useUser, useAuth } from '@clerk/clerk-react';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { initPostHog } from './lib/posthog';
import PricingView from './components/PricingView';
import OnboardingFlow from './components/OnboardingFlow';
import AuthPage from './components/AuthPage';
import ConnectPage from './pages/ConnectPage';
import { ThemeProvider } from './theme/ThemeProvider';
import AppShell from './shell/AppShell';
import { BootLoader } from './ds';
import { EndSessionOnSignOut } from './shell/EndSessionOnSignOut';
import Knowledge from './screens/Knowledge';
import Ledger from './screens/Ledger';
import Authority from './screens/Authority';
import Account from './screens/Account';
import Admin from './screens/Admin';
import Docs from './screens/Docs';
import { useWorkspaceStore } from './stores/workspaceStore';
import { workspaceService } from './services/workspaceService';
import { apiClient, usersApi } from './services/api';
import { isEmbedded, initAppBridge, createDualAuthTokenGetter } from './lib/shopify-app-bridge';
import { CLERK_PUBLISHABLE_KEY, SHOPIFY_API_KEY } from './config/env';

// Initialize PostHog
const posthog = initPostHog();

// Both come from src/config/env.ts, the only module allowed to read import.meta.env.
// The Clerk key used to fall back to 'pk_test_placeholder' here, which is what let a
// keyless build ship green and blank dev.marva.co on 2026-09-17 (MAR-150). There is no
// fallback now — the build refuses instead.
const clerkPubKey = CLERK_PUBLISHABLE_KEY;
const shopifyApiKey = SHOPIFY_API_KEY;

/**
 * Check if current URL path is the connect page (unauthenticated OAuth flow)
 */
function isConnectRoute(): boolean {
  return window.location.pathname === '/connect';
}


// `/today` → `/ledger`, keeping the query string and hash: an old
// `/today?inbox=open` or `?ask=open` link still opens its drawer, as the
// `/notifications` and `/waiting` redirects keep theirs (MAR-309).
function TodayRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/ledger', search, hash }} replace />;
}

// Main authenticated app component
function AuthenticatedApp() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const { user } = useUser();
  const { getToken } = useAuth();
  const tokenGetterSetRef = useRef(false);
  const location = useLocation();

  const ph = usePostHog();

  // Track page views on route changes
  useEffect(() => {
    ph?.capture('$pageview', { path: location.pathname });
  }, [location.pathname, ph]);

  // Identify user in PostHog when Clerk user loads
  useEffect(() => {
    if (user && ph) {
      ph.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      });
    }
  }, [user, ph]);

  // Initialize App Bridge for embedded context
  useEffect(() => {
    if (isEmbedded() && shopifyApiKey) {
      initAppBridge(shopifyApiKey);
    }
  }, []);

  // Configure API client with dual auth (Shopify session token OR Clerk JWT)
  // Only set up once to avoid repeated token fetches from getToken reference changes
  useEffect(() => {
    if (getToken && !tokenGetterSetRef.current) {
      tokenGetterSetRef.current = true;
      // Create dual auth token getter that prefers Shopify session token when embedded
      const dualAuthGetter = createDualAuthTokenGetter(getToken);
      apiClient.setTokenGetter(dualAuthGetter);
    }
  }, [getToken]);

  useEffect(() => {
    // Simulate boot sequence and check if user needs onboarding
    const initializeApp = async() => {
      if (user) {
        try {
          // Clear any stale persisted data for new users
          const currentStoredUser = useWorkspaceStore.getState().userMembership?.clerkUserId;
          if (currentStoredUser && currentStoredUser !== user.id) {
            useWorkspaceStore.getState().clearSession();
          }

          // Link any pending invitations for this user's email
          const userEmail = user.primaryEmailAddress?.emailAddress;
          if (userEmail) {
            try {
              const linkResult = await usersApi.linkInvitation(
                userEmail,
                user.id,
                user.firstName,
                user.lastName
              );
              if (linkResult.success && linkResult.data?.linked && linkResult.data.linked > 0) {
                console.log(`Linked ${linkResult.data.linked} pending invitation(s)`);
              }
            } catch (linkError) {
              // Non-critical error - continue with initialization
              console.warn('Failed to check pending invitations:', linkError);
            }
          }

          // Load user memberships using Clerk user ID (pass publicMetadata for super-admin detection)
          await workspaceService.loadWorkspaceData(user.id, false, user.publicMetadata as Record<string, unknown>);
          const state = useWorkspaceStore.getState();

          // Check if user has completed basic onboarding
          const hasAccount = !!state.currentAccount;
          const hasWorkspace = !!state.currentWorkspace;
          const hasMembership = !!state.userMembership && state.userMembership.accounts.length > 0;

          // Debug logging to understand the issue
          console.log('Onboarding Check:', {
            userId: user.id,
            hasMembership,
            hasAccount,
            hasWorkspace,
            membershipCount: state.userMembership?.accounts?.length || 0
          });

          // Simple logic: User needs onboarding if they don't have all three
          const needsSetup = !hasMembership || !hasAccount || !hasWorkspace;
          setNeedsOnboarding(needsSetup);
        } catch (error) {
          console.error('Failed to load workspace data:', error);
          setNeedsOnboarding(true);
        }
      }

      // Reveal the shell as soon as the real workspace load settles
      // (no artificial boot delay — the rail was lingering behind the splash).
      setIsInitialized(true);
    };

    initializeApp();
  }, [user]);

  const handleOnboardingComplete = async () => {
    // Reload workspace data to get the user's role/permissions
    if (user) {
      try {
        await workspaceService.loadWorkspaceData(user.id, true, user.publicMetadata as Record<string, unknown>);
      } catch (error) {
        console.error('Failed to reload workspace data after onboarding:', error);
      }
    }
    setNeedsOnboarding(false);
  };

  if (!isInitialized) {
    return (
      // The boot loader (design system: BootLoader, at z-loader).
      <BootLoader caption="Catching up on your store…" />
    );
  }

  if (needsOnboarding) {
    return <OnboardingFlow onComplete={handleOnboardingComplete} />;
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* Three Surfaces (v5). Each new route renders the screen it replaces
            until that surface is rebuilt — the IA moves first, the surfaces
            follow one PR at a time. The Ledger is rebuilt (MAR-110);
            `screens/Journal.tsx` stays in the tree, unrouted, until MAR-113. */}
        <Route path="/ledger" element={<Ledger />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/authority" element={<Authority />} />

        {/* The Ask dock replaced this screen, so /ask opens the dock rather
            than landing somewhere unrelated. Old links, bookmarks and the
            pre-dock /chat redirect all still do what they say. */}
        <Route path="/ask" element={<Navigate to="/ledger?ask=open" replace />} />
        <Route path="/account" element={<Account />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/profile" element={<Navigate to="/account?tab=profile" replace />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/pricing" element={<PricingView />} />

        {/* Today is retired (MAR-309). v5 dissolved it into Ledger: the
            "waiting on you" half is the Inbox drawer plus Ledger's waiting and
            Shadow banners, the loading beat is Ledger's, and the value tiles
            are Ledger's attribution strip (from GET /attribution — Today's
            decision/handled/approval tiles were counted in the browser from
            100 rows and were not ported). Old links and bookmarks land on
            Ledger. */}
        <Route path="/today" element={<TodayRedirect />} />
        {/* Old IA → new IA redirects */}
        {/* Pre-v5 IA — every old path keeps working. */}
        <Route path="/" element={<Navigate to="/ledger" replace />} />
        <Route path="/dashboard" element={<Navigate to="/ledger" replace />} />
        <Route path="/journal" element={<Navigate to="/ledger" replace />} />
        <Route path="/activity" element={<Navigate to="/ledger" replace />} />
        <Route path="/attribution" element={<Navigate to="/ledger" replace />} />
        <Route path="/memory" element={<Navigate to="/knowledge" replace />} />
        <Route path="/data" element={<Navigate to="/knowledge" replace />} />
        <Route path="/messaging" element={<Navigate to="/authority" replace />} />
        {/* The Inbox drawer replaced this screen, so /notifications opens the
            drawer rather than landing somewhere unrelated. NotificationsSection's
            prose link points here and still does what it says. */}
        <Route path="/notifications" element={<Navigate to="/ledger?inbox=open" replace />} />
        <Route path="/waiting" element={<Navigate to="/ledger?inbox=open" replace />} />
        <Route path="/chat" element={<Navigate to="/ledger?ask=open" replace />} />
        <Route path="/setup" element={<Navigate to="/account" replace />} />
        <Route path="/billing" element={<Navigate to="/account?tab=billing" replace />} />
        <Route path="/team" element={<Navigate to="/account?tab=team" replace />} />
        <Route path="*" element={<Navigate to="/ledger" replace />} />
      </Route>
    </Routes>
  );
}

// Root App component with Clerk provider
function App() {
  // Handle unauthenticated routes (OAuth connect flow)
  if (isConnectRoute()) {
    return (
      <ConnectPage
        onBack={() => {
          window.location.href = '/';
        }}
      />
    );
  }

  return (
    <ThemeProvider>
      <PostHogProvider client={posthog}>
        <BrowserRouter>
          <ClerkProvider publishableKey={clerkPubKey}>
            {/* Every sign-out route (menu, expiry, revocation) ends the store's session here. */}
            <EndSessionOnSignOut />
            <SignedOut>
              {/* Marva-branded auth (After Hours). Path-routed so Clerk owns its
                  own sub-steps (factor-one, sso-callback). Any other path while
                  signed out lands on /sign-in. */}
              <Routes>
                <Route path="/sign-in/*" element={<AuthPage flow="sign-in" />} />
                <Route path="/sign-up/*" element={<AuthPage flow="sign-up" />} />
                <Route path="*" element={<Navigate to="/sign-in" replace />} />
              </Routes>
            </SignedOut>
            <SignedIn>
              <AuthenticatedApp />
            </SignedIn>
          </ClerkProvider>
        </BrowserRouter>
      </PostHogProvider>
    </ThemeProvider>
  );
}

export default App;
